import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { toast } from 'react-toastify';

import { DropDownOptions } from 'devextreme-react/autocomplete';
import Form, { Item, RequiredRule } from 'devextreme-react/form';
import ValidationGroup from 'devextreme-react/validation-group';
import Validator from 'devextreme-react/validator';

import { AppCol, AppFieldBox, AppLayoutTop } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { MONEY_FORMAT } from 'constants/formats';
import { Button, DateBox, NumberBox, SelectBox, TabPanel, TextArea } from 'devextreme-react';
import $createDataSource from 'utils/create-data-souce-instance';

import {
  useChangeCashFlowExpenseSubReportMutation,
  useCreateCashFlowExpenseSubReportMutation,
  useGetCashFlowAccountsQuery,
  useLazyGetCashFlowExpenseSubReportQuery,
} from 'bus/cash';

import { CashFlowExpenseSubReportDetail, CashFlowExpenseSubReportExtraditionsDetail } from './components';
import { initValue } from './utils';

const itemTitleRender = (tab: { name: string; index: number }) => {
  return <span>{tab.name}</span>;
};

const tabs = [
  {
    name: 'Выдано',
    index: 0,
  },
  {
    name: 'Подтвержденно',
    index: 1,
  },
];

const CashFlowExpenseSubReport: FC = () => {
  const history = useHistory();
  let { id } = useParams<{ id: string }>();

  const [form, setForm] = useState(initValue);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const createValidationRef = useRef<ValidationGroup>(null);

  const [lazyGetCashFlowExpenseSubReport] = useLazyGetCashFlowExpenseSubReportQuery();
  const [changeCashFlowExpenseSubReport, { isLoading: isLoadingChange }] = useChangeCashFlowExpenseSubReportMutation();
  const [createCashFlowExpenseSubReport, { isLoading: isLoadingCreate }] = useCreateCashFlowExpenseSubReportMutation();
  const { data: cashFlowAccounts } = useGetCashFlowAccountsQuery();

  const disabledSubmit = isLoadingChange || isLoadingCreate;

  const handleSubmit = async (e: any) => {
    e.preventDefault && e.preventDefault();

    if (createValidationRef.current?.instance.validate().isValid) {
      if (id) {
        await changeCashFlowExpenseSubReport({ id, ...form });
        toast.success('Изменения успешно сохранены', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        await createCashFlowExpenseSubReport(form);
        history.push('/cash-flow');
      }
    }
  };

  const employeeDateSource = useMemo(() => {
    return $createDataSource('/v1/employee', 'id');
  }, []);

  const getInit = useCallback(async () => {
    if (id) {
      const result = await lazyGetCashFlowExpenseSubReport({ id });
      result && setForm(result.data!);
    }
  }, [id, lazyGetCashFlowExpenseSubReport]);

  useEffect(() => {
    getInit();
  }, [getInit]);

  return (
    <div className="mt-5">
      <div style={{ width: 750 }} className="mb-5">
        <form onSubmit={handleSubmit}>
          <ValidationGroup ref={createValidationRef}>
            <AppLayoutTop title="Расходы в подотчет">
              <ClaimsProvider claimsBlock={['CanEditExpenseSubReport']}>
                <Button
                  text="Сохранить"
                  type="default"
                  useSubmitBehavior={false}
                  onClick={handleSubmit}
                  disabled={disabledSubmit}
                />
              </ClaimsProvider>
            </AppLayoutTop>
            <div className="dx-card">
              <Form formData={form}>
                <Item colSpan={12} colCount={12} itemType="group">
                  <AppCol col={4}>
                    <AppFieldBox label="Дата" labelTop>
                      <DateBox
                        stylingMode="underlined"
                        type="date"
                        onValueChange={(e) => setForm((prev) => ({ ...prev, date: e as string }))}
                        useMaskBehavior
                        dateSerializationFormat="yyyy-MM-dd"
                        value={form.date}
                      >
                        <Validator>
                          <RequiredRule message="Заполните поле Дата" />
                        </Validator>
                      </DateBox>
                    </AppFieldBox>
                  </AppCol>

                  <AppCol col={4}>
                    <AppFieldBox label="ФИО" labelTop>
                      <SelectBox
                        stylingMode="underlined"
                        dataSource={employeeDateSource}
                        displayExpr="fullName"
                        valueExpr="id"
                        value={form.employeeId}
                        searchEnabled
                        onItemClick={(e) => {
                          setForm((prev) => ({ ...prev, employeeId: e.itemData.id }));
                        }}
                      >
                        <DropDownOptions title="Выберите контрагента" closeOnOutsideClick={true} showTitle={false} />
                        <Validator>
                          <RequiredRule message="Выберите контрагента" />
                        </Validator>
                      </SelectBox>
                    </AppFieldBox>
                  </AppCol>

                  <AppCol col={4}>
                    <AppFieldBox label="Статья списания" labelTop>
                      <SelectBox
                        stylingMode="underlined"
                        items={cashFlowAccounts}
                        displayExpr="name"
                        valueExpr="id"
                        value={form.cashFlowAccount}
                        searchEnabled
                        onItemClick={(e) => {
                          setForm((prev) => ({ ...prev, cashFlowAccount: e.itemData.id }));
                        }}
                      >
                        <DropDownOptions
                          title="Выберите Статья списания"
                          closeOnOutsideClick={true}
                          showTitle={false}
                        />
                        <Validator>
                          <RequiredRule message="Выберите Статья списания" />
                        </Validator>
                      </SelectBox>
                    </AppFieldBox>
                  </AppCol>

                  <AppCol col={4}>
                    <AppFieldBox label="Выдано, руб." labelTop>
                      <NumberBox
                        stylingMode="underlined"
                        placeholder="0"
                        format={MONEY_FORMAT}
                        value={form.sum}
                        onValueChanged={(e) => {
                          setForm((prev) => ({ ...prev, sum: e.value }));
                        }}
                      >
                        <Validator>
                          <RequiredRule message="Заполните поле Выдано, руб." />
                        </Validator>
                      </NumberBox>
                    </AppFieldBox>
                  </AppCol>
                  {id && (
                    <>
                      <AppCol col={4}>
                        <AppFieldBox label="Подтверждено, руб." labelTop>
                          <NumberBox
                            stylingMode="underlined"
                            placeholder="0"
                            format={MONEY_FORMAT}
                            value={form.confirmedSum}
                            readOnly
                          ></NumberBox>
                        </AppFieldBox>
                      </AppCol>

                      <AppCol col={4}>
                        <AppFieldBox label="Остаток, руб." labelTop>
                          <NumberBox
                            stylingMode="underlined"
                            placeholder="0"
                            format={MONEY_FORMAT}
                            value={form.sum - form.confirmedSum}
                            readOnly
                            style={{ background: form.sum - form.confirmedSum !== 0 ? '#ffc5c5' : 'white' }}
                          ></NumberBox>
                        </AppFieldBox>
                      </AppCol>
                    </>
                  )}

                  <AppCol col={12}>
                    <AppFieldBox label="Комментарий" labelTop>
                      <TextArea
                        stylingMode="underlined"
                        placeholder="Введите комментарий"
                        mode="text"
                        value={form.comment || ''}
                        onChange={(e: any) => {
                          setForm((prev) => ({ ...prev, comment: e.event.currentTarget.value }));
                        }}
                      />
                    </AppFieldBox>
                  </AppCol>
                </Item>
              </Form>
            </div>
          </ValidationGroup>
        </form>
      </div>
      <ClaimsProvider claimsBlock={['CanLookExpenseSubReportDetails']}>
        {id && (
          <>
            <TabPanel
              height="auto"
              dataSource={tabs}
              selectedIndex={selectedIndex}
              onOptionChanged={(e) => {
                if (e.name === 'selectedIndex') {
                  setSelectedIndex(e.value);
                }
              }}
              itemTitleRender={itemTitleRender}
              animationEnabled={false}
            />

            {selectedIndex === 0 && (
              <div className="tab-content">
                <CashFlowExpenseSubReportExtraditionsDetail cashFlowId={id} refreshForm={getInit} />
              </div>
            )}
            {selectedIndex === 1 && (
              <div className="tab-content">
                <CashFlowExpenseSubReportDetail cashFlowId={id} refreshForm={getInit} />
              </div>
            )}
          </>
        )}
      </ClaimsProvider>
    </div>
  );
};

export default CashFlowExpenseSubReport;
